<script setup lang="ts">
import { type Skin, SKIN_CATEGORIES } from '@/types/skin'
import { computed, onMounted, ref, watch } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import PrizeCard from '@/components/PrizeCard.vue'
import { CARD_COLORS } from '@/enums/common'
import { twMerge } from 'tailwind-merge'
import { T, useTranslate } from '@tolgee/vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import { useSkin } from '@/composables/game/skin'
import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import LockIcon from '@/components/icons/LockIcon.vue'
import SkinDetailsModal from '@/components/modals/SkinDetailsModal.vue'
import { useConfetti } from '@/composables/game/confetti'
import { useToaster } from '@/composables/game/toasters'
import TabButton from '@/components/TabButton.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useShop } from '@/composables/game/shop'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import DefaultBanner from '@/components/periodic/DefaultBanner.vue'
import type { BannerVariant } from '@/components/periodic/DefaultBanner.vue'
import DefaultTag from '@/components/periodic/DefaultTag.vue'
import type { TagVariant } from '@/components/periodic/DefaultTag.vue'

const {
  currentSkin,
  ownedSkinIds,
  selectSkin,
  getSkinById,
  getSkinShopItemById,
  basicSkins,
  premiumSkins,
  epicSkins,
  loadingSkin,
  canHaveSale,
  hasSkinDiscount,
} = useSkin()
const { showConfetti } = useConfetti()
const { notify } = useToaster()
const { t } = useTranslate()
const { addHapticFeedback } = useHapticFeedback()
const { saleValue, saleName, countdownForSaleEnds, isDiscount, isDefaultDiscount, isIWD, isSTP } = useShop()
const { topNumber } = useFullScreenDimensions()

const selectedSkin = ref<Skin>(currentSkin.value)
const selectedForDetailsSkin = ref<Skin | null>(null)
const selectedShopItem = ref<EcommerceItem | undefined>()
const showSkinDetailsModal = ref(false)

const hasDiscount = computed(() => canHaveSale.value && isDiscount.value)

const skinScrollContainer = ref<HTMLElement | null>(null)

const activeCategory = ref(SKIN_CATEGORIES.BASIC)

const skinCardColorMap: Record<SKIN_CATEGORIES, CARD_COLORS> = {
  [SKIN_CATEGORIES.BASIC]: CARD_COLORS.GRAY,
  [SKIN_CATEGORIES.PREMIUM]: CARD_COLORS.PINK,
  [SKIN_CATEGORIES.EPIC]: CARD_COLORS.YELLOW,
}

const skinsToShow = computed(() => {
  switch (activeCategory.value) {
  case SKIN_CATEGORIES.BASIC: {
    return basicSkins.value
  }
  case SKIN_CATEGORIES.PREMIUM: {
    return premiumSkins.value
  }
  case SKIN_CATEGORIES.EPIC: {
    return epicSkins.value
  }
  default: {
    return basicSkins.value
  }
  }
})

const topStyles = computed(() => {
  return { top: `-${topNumber.value}px` }
})

const saleVariant = computed<BannerVariant | TagVariant>(() => {
  if (isDefaultDiscount.value) return 'discount'
  if (isIWD.value) return 'IWD'
  if (isSTP.value) return 'STP'
  return 'discount' // fallback
})

function getSkinPriceInStars(skinId: string) {
  const skin = getSkinById(skinId)
  if (skin) {
    const priceItem = skin.price.find((priceItem) => priceItem.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
    if (priceItem) {
      return priceItem.amount
    }
    throw new Error('Skin price not found.')
  }
  return 0
}

function getSkinPriceInUSD(skinId: string) {
  const skin = getSkinById(skinId)
  if (skin) {
    const priceItem = skin.price.find((priceItem) => priceItem.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
    if (priceItem) {
      return priceItem.usdAmount
    }
    throw new Error('Skin price not found.')
  }
  return 0
}

function getSkinOriginalPriceInUSD(skinId: string) {
  const skin = getSkinById(skinId)
  if (skin) {
    const priceItem = skin.price.find((priceItem) => priceItem.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
    if (priceItem) {
      return priceItem.originalUsdAmount
    }
    throw new Error('Skin price not found.')
  }
  return 0
}

function getFormattedPrice(amount: number) {
  return `$${formatNumberFullTruncate(amount, true)}`
}

function getFormattedSkinPriceInUSD(skinId: string) {
  const amount = getSkinPriceInUSD(skinId)
  return getFormattedPrice(amount)
}

function getFormattedSkinDiscountPriceInUSD(skinId: string) {
  const amount = getSkinOriginalPriceInUSD(skinId)
  return getFormattedPrice(amount)
}

function closeSkinDetailModal() {
  showSkinDetailsModal.value = false
  selectedForDetailsSkin.value = null
}

async function onPurchase() {
  closeSkinDetailModal()
  notify({ title: t.value('skin.purchase.success', 'Skin successfully unlocked!'), type: 'success' })
  await showConfetti()
}

function onSkinSelected(skin: Skin) {
  selectedSkin.value = skin
  if (!skinScrollContainer.value) return
  skinScrollContainer.value.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

watch(selectedForDetailsSkin, () => {
  if (selectedForDetailsSkin.value) {
    selectedShopItem.value = getSkinShopItemById(selectedForDetailsSkin.value.id)
    showSkinDetailsModal.value = true
  } else {
    selectedShopItem.value = undefined
  }
}, { immediate: true, deep: true })

onMounted(() => {
  if (!skinScrollContainer.value) {
    skinScrollContainer.value = document.querySelector('.scroll-container')
  }
  if (!skinScrollContainer.value) return
})
</script>
<template>
  <div
    class="flex flex-col items-center px-4 py-6 relative"
  >
    <div />
    <div
      v-if="hasDiscount"
      class="absolute w-screen overflow-hidden h-[300px] left-0 pointer-events-none flex justify-center"
      :style="topStyles"
    >
      <DefaultBanner
        :variant="saleVariant"
        :name="saleName"
        :countdown="countdownForSaleEnds"
      />
    </div>
    <DefaultTag
      v-if="hasDiscount"
      :variant="saleVariant"
      class="absolute left-[calc(50%+70px)] top-[90px]"
    />
    <img
      :src="selectedSkin.imageUrl"
      class="size-[180px] mb-6"
    >
    <div
      class="flex w-full flex-col items-center text-center mb-6"
    >
      <h2 class="mb-1 text-white text-2xl font-bold">
        {{ selectedSkin.name }}
      </h2>
      <p class="mb-3 text-lg font-medium text-white/60">
        {{ selectedSkin.description }}
      </p>
      <BaseButton
        v-if="selectedSkin.id === currentSkin.id"
        class="w-full"
        disabled
      >
        <T
          key-name="skins.currently_used"
          default-value="CURRENTLY USED"
        />
      </BaseButton>
      <BaseButton
        v-else-if="ownedSkinIds.includes(selectedSkin.id)"
        class="w-full"
        :loading="loadingSkin"
        @click="() => selectSkin(selectedSkin.id)"
      >
        <T
          key-name="skins.use_this_skin"
          default-value="Use this skin"
        />
      </BaseButton>
      <BaseButton
        v-else
        style-type="secondary"
        class="w-full"
        :loading="loadingSkin"
        @click="selectedForDetailsSkin = selectedSkin"
      >
        <T
          v-if="!getSkinPriceInStars(selectedSkin.id)"
          key-name="skins.unlock_for_free"
          default-value="Unlock for free"
        />
        <div
          v-else
          class="flex items-center"
        >
          <span class="uppercase mr-2">
            <T
              key-name="skins.buy_with"
              default-value="Buy for"
            />
          </span>
          <span>{{ getFormattedSkinPriceInUSD(selectedSkin.id) }}</span>
        </div>
      </BaseButton>
    </div>
    <div
      :class="twMerge('flex w-full rounded-xl p-0.5 bg-dark-top-layer z-[2] relative mb-6')"
    >
      <TabButton
        :is-active="activeCategory === SKIN_CATEGORIES.BASIC"
        @click="addHapticFeedback(); activeCategory = SKIN_CATEGORIES.BASIC"
      >
        <T
          key-name="skins.categories.basic"
          default-value="Basic"
        />
      </TabButton>
      <TabButton
        :is-active="activeCategory === SKIN_CATEGORIES.PREMIUM"
        @click="addHapticFeedback(); activeCategory = SKIN_CATEGORIES.PREMIUM"
      >
        <T
          key-name="skins.categories.premium"
          default-value="Premium"
        />
      </TabButton>
      <TabButton
        :is-active="activeCategory === SKIN_CATEGORIES.EPIC"
        @click="addHapticFeedback(); activeCategory = SKIN_CATEGORIES.EPIC"
      >
        <T
          key-name="skins.categories.epic"
          default-value="Epic"
        />
      </TabButton>
    </div>
    <div class="grid grid-cols-3 gap-3">
      <PrizeCard
        v-for="skin in skinsToShow"
        :key="skin.id"
        :color="skinCardColorMap[activeCategory]"
        :card-class="twMerge('before:hidden border-[3px] border-[#545351]',
                             skin.id === selectedSkin.id && 'border-white',
                             skin.id === currentSkin.id && 'border-teal-primary',

        )"
        @click="onSkinSelected(skin)"
      >
        <div
          class="flex flex-col items-center h-full pt-[3px] relative w-full"
        >
          <LockIcon
            v-if="!ownedSkinIds.includes(skin.id)"
            class="absolute top-[3px] right-[3px] size-6 text-[#A7A7A5]"
          />
          <span
            class="font-bold text-white text-[21px] leading-[30px] uppercase black-stroke pt-[3px] mb-3 flex-[0_0_auto]"
          >
            {{ skin.name }}</span>
          <div class="w-full flex h-full justify-center items-center flex-1 mb-[9px] overflow-hidden">
            <img
              :src="skin.imageUrl"
              class="object-contain max-w-full max-h-full"
            >
          </div>

          <div class="w-full relative pt-2 pb-1.5 flex items-center justify-center flex-[0_0_auto]">
            <div class="text-[21px] leading-[30px] font-bold text-white/[87%] z-10">
              <div v-if="skin.id === currentSkin.id">
                <CheckIcon class="text-teal-primary size-[30px]" />
              </div>
              <div
                v-else-if="ownedSkinIds.includes(skin.id)"
                class="h-[30px]"
              />
              <span v-else-if="!getSkinPriceInUSD(skin.id)">
                <T
                  key-name="skins.free"
                  default-value="Free"
                />
              </span>
              <span
                v-else-if="hasDiscount && hasSkinDiscount(skin.id)"
                class="disable-slashed-zero flex items-center gap-x-1"
              >
                <span class="line-through text-white/[44%] text-base">{{
                  getFormattedSkinDiscountPriceInUSD(skin.id)
                }}</span>
                <span class="text-white">
                  {{
                    getFormattedSkinPriceInUSD(skin.id)
                  }}
                </span>
              </span>
              <span
                v-else
                class="text-white disable-slashed-zero"
              >
                {{
                  getFormattedSkinPriceInUSD(skin.id)
                }}
              </span>
            </div>
            <div class="absolute top-0 left-1/2 -translate-x-1/2 bg-black/20 w-[calc(100%+16px)] h-[calc(100%+8px)]" />
          </div>
        </div>
      </PrizeCard>
    </div>
  </div>

  <SkinDetailsModal
    v-if="showSkinDetailsModal && selectedShopItem && selectedForDetailsSkin"
    :show="showSkinDetailsModal"
    :skin="selectedForDetailsSkin"
    :item="selectedShopItem"
    @hide="closeSkinDetailModal"
    @on-purchase="onPurchase"
  />
</template>
<style scoped lang="scss">
@import '@/assets/styles/sales.scss';

</style>
